import type { GameSettings } from "../game/GameConfig";
import { toggleDocumentFullscreen } from "./Fullscreen";
import { createButton, createElement } from "./dom";

export class SettingsMenu {
  public readonly element = createElement("section", "screen overlay-screen hidden");
  private readonly controls = new Map<keyof GameSettings, HTMLInputElement>();

  public constructor(
    settings: GameSettings,
    onChange: (settings: GameSettings) => void,
    onClose: () => void,
  ) {
    this.element.append(
      createElement("div", "eyebrow", "НАСТРОЙКА ЛОКАЛЬНОГО ТЕРМИНАЛА"),
      createElement("h2", undefined, "НАСТРОЙКИ"),
    );
    const form = createElement("div", "settings-grid");
    this.addRange(
      form,
      "ЧУВСТВИТЕЛЬНОСТЬ МЫШИ",
      "mouseSensitivity",
      0.0005,
      0.004,
      0.0001,
      settings.mouseSensitivity,
    );
    this.addRange(form, "ОБЩАЯ ГРОМКОСТЬ", "masterVolume", 0, 1, 0.05, settings.masterVolume);
    this.addRange(
      form,
      "ЧУВСТВИТЕЛЬНОСТЬ КАСАНИЙ",
      "touchSensitivity",
      0.5,
      2,
      0.1,
      settings.touchSensitivity,
    );
    this.addRange(form, "ГРОМКОСТЬ ЭФФЕКТОВ", "effectsVolume", 0, 1, 0.05, settings.effectsVolume);
    this.addRange(
      form,
      "ГРОМКОСТЬ ОКРУЖЕНИЯ",
      "ambienceVolume",
      0,
      1,
      0.05,
      settings.ambienceVolume,
    );
    const bobRow = createElement("label", "setting-row");
    bobRow.append(createElement("span", undefined, "ПОКАЧИВАНИЕ КАМЕРЫ"));
    const bob = createElement("input");
    bob.type = "checkbox";
    bob.checked = settings.cameraBob;
    this.controls.set("cameraBob", bob);
    bobRow.append(bob);
    form.append(bobRow);

    const qualityRow = createElement("label", "setting-row");
    qualityRow.append(createElement("span", undefined, "КАЧЕСТВО ГРАФИКИ"));
    const quality = createElement("select");
    for (const [value, label] of [
      ["auto", "Авто"],
      ["low", "Низкое · экономия GPU"],
      ["balanced", "Стандарт · ретро"],
      ["high", "Высокое · чётче"],
    ]) {
      const option = createElement("option", undefined, label);
      option.value = value ?? "auto";
      quality.append(option);
    }
    quality.value = settings.quality;
    qualityRow.append(quality);
    form.append(qualityRow);

    const apply = () => {
      onChange({
        mouseSensitivity: Number(this.controls.get("mouseSensitivity")?.value),
        masterVolume: Number(this.controls.get("masterVolume")?.value),
        effectsVolume: Number(this.controls.get("effectsVolume")?.value),
        ambienceVolume: Number(this.controls.get("ambienceVolume")?.value),
        cameraBob: this.controls.get("cameraBob")?.checked ?? true,
        touchSensitivity: Number(this.controls.get("touchSensitivity")?.value),
        quality: quality.value as GameSettings["quality"],
      });
    };
    form.addEventListener("input", apply);
    const buttons = createElement("div", "settings-actions");
    const fullscreenStatus = createElement("p", "settings-note");
    fullscreenStatus.setAttribute("role", "status");
    const fullscreen = createButton("ПОЛНЫЙ ЭКРАН", () => {
      fullscreenStatus.textContent = "";
      void toggleDocumentFullscreen().then((result) => {
        if (result === "unsupported") {
          fullscreenStatus.textContent =
            "Браузер не поддерживает полный экран. Поверните устройство вручную.";
        } else if (result === "failed") {
          fullscreenStatus.textContent = "Полный экран недоступен в этом браузере.";
        }
      });
    });
    buttons.append(fullscreen, createButton("НАЗАД", onClose));
    this.element.append(form, buttons, fullscreenStatus);
  }

  public show(): void {
    this.element.classList.remove("hidden");
  }

  public hide(): void {
    this.element.classList.add("hidden");
  }

  private addRange(
    parent: HTMLElement,
    label: string,
    key: keyof GameSettings,
    min: number,
    max: number,
    step: number,
    value: number,
  ): void {
    const row = createElement("label", "setting-row");
    row.append(createElement("span", undefined, label));
    const input = createElement("input");
    input.type = "range";
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(value);
    this.controls.set(key, input);
    row.append(input);
    parent.append(row);
  }
}
